/** Import Marketist CSS */
/** Mock service worker. */
import '../mocks'

import '@doist/marketist/dist/index.css'

import '../styles/reset.css'
import '../styles/fonts.css'
import '../styles/global.css'

import { useEffect } from 'react'

import {
    NextContextProvider,
    Theme,
    ThemeProvider,
    URLContext,
    UserContext,
} from '@doist/marketist'

/* To fix the css import order warning: */
/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
import * as _components from 'components'
import { ErrorBoundary } from 'components/ErrorBoundary/ErrorBoundary'
import { TwistDefaultSeo } from 'components/Seo'
import { AppProps } from 'next/app'
import Head from 'next/head'
// eslint-disable-next-line no-restricted-imports
import NextImage from 'next/image'
import { useRouter } from 'next/router'
import { appWithTranslation } from 'next-i18next'
import { flagsTracking } from 'services/feature-flags/flags-tracking'

import nextI18NextConfig from '../next-i18next.config.js'

import css from './_app.module.css'

const nextContextValues = {
    components: {
        head: Head,
        image: NextImage,
    },
}

function LandingPagesApp({ Component, pageProps }: AppProps) {
    const router = useRouter()

    useEffect(() => {
        const { featureFlags } = pageProps
        if (featureFlags?.flags) {
            flagsTracking(featureFlags.flags)
        }
        // Reason: evaluated server side and shouldn't change and only want this to sync on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /**
     * If client side loading happens, instead of allowing the Component to load with insufficient
     * data, causing error, return empty page until page finishes reloading from server. This should
     * only happen in the case of the Safari bug mentioned above.
     */
    if (pageProps.clientSideLoading) return <></>

    const { isAuthenticated, webpSupport, absoluteUrl } = pageProps
    const userContextValue = { isAuthenticated, webpSupport }

    return (
        <ErrorBoundary>
            <UserContext.Provider value={userContextValue}>
                <NextContextProvider value={nextContextValues}>
                    <URLContext.Provider value={absoluteUrl}>
                        <ThemeProvider theme={Theme.twist}>
                            <Head>
                                <meta
                                    name="facebook-domain-verification"
                                    content={process.env.NEXT_PUBLIC_FACEBOOK_DOMAIN_VERIFICATION}
                                />
                            </Head>
                            <TwistDefaultSeo />

                            <div className={css.appContainer}>
                                <Component query={router.query} {...pageProps} />
                            </div>
                        </ThemeProvider>
                    </URLContext.Provider>
                </NextContextProvider>
            </UserContext.Provider>
        </ErrorBoundary>
    )
}

export default appWithTranslation(LandingPagesApp, nextI18NextConfig)
